// web/src/pages/email/SentEmailPage.tsx
//
// Task 12 — Staff-facing outbound email log (`/api/email/sent`), gated
// `email:read-all`. Mirrors the admin EmailLogPage chrome (ListPage + server
// paginated DataGrid + shared EmailMessageViewerDialog on row click), but
// reads from the newer `emailApi.sent` endpoint (Task 8) which already
// carries a resolved `status` string alongside `sentSuccessfully`.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { emailApi, type SentEmailItem } from "@/lib/email";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { EmailMessageViewerDialog } from "@/components/email/EmailMessageViewerDialog";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 50;

type StatusChipId = "all" | "Sent" | "Failed" | "Bounced" | "Queued";

const STATUS_CHIP_OPTIONS: ReadonlyArray<{ id: StatusChipId; label: string }> = [
  { id: "all", label: "All" },
  { id: "Sent", label: "Sent" },
  { id: "Failed", label: "Failed" },
  { id: "Bounced", label: "Bounced" },
  { id: "Queued", label: "Queued" },
];

const ENTITY_ROUTE: Record<string, string> = {
  Quote: "/quotes",
  Job: "/jobs",
  Invoice: "/invoices",
  Account: "/accounts",
  Client: "/clients",
};

function statusBadge(item: SentEmailItem) {
  const label = item.status ?? (item.sentSuccessfully ? "Sent" : "Failed");
  const isBad = item.status
    ? item.status === "Failed" || item.status === "Bounced"
    : !item.sentSuccessfully;
  return <Badge variant={isBad ? "destructive" : "success"}>{label}</Badge>;
}

function aboutCell(item: SentEmailItem) {
  if (!item.relatedEntityType || item.relatedEntityId == null) return "—";
  const base = ENTITY_ROUTE[item.relatedEntityType];
  const label = `${item.relatedEntityType} #${item.relatedEntityId}`;
  if (!base) return label;
  return (
    <Link
      to={`${base}/${item.relatedEntityId}`}
      className="underline-offset-2 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
}

export function SentEmailPage() {
  const [searchText, setSearchText] = useState("");
  const [statusChip, setStatusChip] = useState<StatusChipId>("all");
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);

  const status = statusChip === "all" ? undefined : statusChip;

  const { data, isLoading } = useQuery({
    queryKey: ["email-sent-list", { q: searchText, status, page }],
    queryFn: () => emailApi.sent({ q: searchText || undefined, status, page, pageSize: PAGE_SIZE }),
  });

  const cols: DataGridColumn<SentEmailItem>[] = [
    { id: "sentAt", header: "Sent at", accessor: (r) => formatDateTime(r.sentAtUtc) },
    { id: "to", header: "To", accessor: (r) => r.toEmailAddress },
    {
      id: "subject",
      header: "Subject",
      accessor: (r) => (
        <button
          type="button"
          className="text-left underline-offset-2 hover:underline"
          onClick={() => setOpenId(r.emailLogId)}
        >
          {r.subject}
        </button>
      ),
    },
    { id: "kind", header: "Type", accessor: (r) => r.kind },
    {
      id: "entity",
      header: "About",
      accessor: aboutCell,
    },
    { id: "status", header: "Status", accessor: statusBadge },
  ];

  return (
    <ListPage
      title="Sent email"
      search={{
        value: searchText,
        onChange: (v) => {
          setSearchText(v);
          setPage(0);
        },
        placeholder: "Search sent email…",
      }}
      filterChips={{
        options: STATUS_CHIP_OPTIONS,
        value: statusChip,
        onChange: (next) => {
          setStatusChip(next);
          setPage(0);
        },
        "aria-label": "Sent email status filter",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.emailLogId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        emptyHeadline="No sent email in this range."
        onPageChange={setPage}
        onRowClick={(r) => setOpenId(r.emailLogId)}
      />

      <EmailMessageViewerDialog
        emailLogId={openId}
        open={openId != null}
        onOpenChange={(o) => {
          if (!o) setOpenId(null);
        }}
      />
    </ListPage>
  );
}
